import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Video, MapPin, Clock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const DAYS = [
  { k: "0", name: "Monday" },
  { k: "1", name: "Tuesday" },
  { k: "2", name: "Wednesday" },
  { k: "3", name: "Thursday" },
  { k: "4", name: "Friday" },
  { k: "5", name: "Saturday" },
  { k: "6", name: "Sunday" },
];

// Show hours 0..23 → label "7am", "8am", "4pm" etc.
const HOUR_LABEL = (h) => {
  if (h === 0) return "12am";
  if (h === 12) return "12pm";
  return h < 12 ? `${h}am` : `${h - 12}pm`;
};

export default function WorkingHours() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/locations");
      setLocations(data);
      const d = {};
      data.forEach((l) => { d[l.id] = { ...(l.schedule || {}) }; });
      setDrafts(d);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleDay = (locId, k) => {
    setDrafts((prev) => {
      const cur = { ...(prev[locId] || {}) };
      if (cur[k]) delete cur[k];
      else cur[k] = [9, 17];
      return { ...prev, [locId]: cur };
    });
  };

  const setHour = (locId, k, idx, val) => {
    setDrafts((prev) => {
      const cur = { ...(prev[locId] || {}) };
      const range = cur[k] || [9, 17];
      const newVal = Math.max(0, Math.min(24, Number(val) || 0));
      cur[k] = idx === 0 ? [newVal, Math.max(range[1], newVal + 1)] : [range[0], Math.max(range[0] + 1, newVal)];
      return { ...prev, [locId]: cur };
    });
  };

  const isDirty = (loc) => JSON.stringify(loc.schedule || {}) !== JSON.stringify(drafts[loc.id] || {});

  const save = async (loc) => {
    setSavingId(loc.id);
    try {
      await api.patch(`/admin/locations/${loc.id}`, { schedule: drafts[loc.id] });
      toast.success(`${loc.name} hours saved`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const presets = (locId) => ({
    weekdays: { "0": [9, 17], "1": [9, 17], "2": [9, 17], "3": [9, 17], "4": [9, 17] },
    everyDay: { "0": [9, 17], "1": [9, 17], "2": [9, 17], "3": [9, 17], "4": [9, 17], "5": [9, 17], "6": [9, 17] },
    none: {},
  });
  const applyPreset = (locId, preset) => {
    setDrafts((prev) => ({ ...prev, [locId]: presets()[preset] }));
  };

  if (loading) {
    return <div className="py-16 flex items-center justify-center text-ink-muted"><Loader2 size={20} className="animate-spin mr-2" /> Loading…</div>;
  }

  return (
    <div data-testid="working-hours" className="space-y-8">
      <div>
        <div className="flex items-start gap-3 text-sm text-ink-muted bg-cream-soft border border-sand/60 rounded-2xl p-4 mb-2">
          <Clock size={16} className="mt-0.5 text-navy" />
          <div>
            <div className="font-medium text-ink">Set when each location accepts bookings.</div>
            <div className="text-xs">Uncheck a day to close it (e.g. "no Tuesdays"). Use the start/end pickers to set hours (e.g. "Wed until 4pm" → 9 to 16). These hours apply to public bookings — admin-created bookings can still override.</div>
          </div>
        </div>
      </div>

      {locations.map((loc) => {
        const draft = drafts[loc.id] || {};
        const dirty = isDirty(loc);
        return (
          <div key={loc.id} data-testid={`hours-loc-${loc.slug}`} className="bg-cream-soft border border-sand/70 rounded-3xl p-6">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-navy mb-1">
                  {loc.type === "online" ? <Video size={13} /> : <MapPin size={13} />}
                  {loc.type === "online" ? "Telehealth" : "In person"}
                </div>
                <div className="font-serif text-2xl text-ink">{loc.name}</div>
                {loc.address && <div className="text-xs text-ink-soft mt-0.5">{loc.address}</div>}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => applyPreset(loc.id, "weekdays")} className="text-xs rounded-full border border-sand bg-cream px-3 py-1.5 hover:border-navy">Mon–Fri 9–5</button>
                <button onClick={() => applyPreset(loc.id, "everyDay")} className="text-xs rounded-full border border-sand bg-cream px-3 py-1.5 hover:border-navy">Every day 9–5</button>
                <button onClick={() => applyPreset(loc.id, "none")} className="text-xs rounded-full border border-sand bg-cream px-3 py-1.5 hover:border-red-300 hover:text-red-700">All closed</button>
              </div>
            </div>

            <div className="space-y-2">
              {DAYS.map((d) => {
                const range = draft[d.k];
                const open = !!range;
                return (
                  <div
                    key={d.k}
                    data-testid={`hours-${loc.slug}-${d.k}`}
                    className="flex items-center gap-4 bg-cream rounded-xl p-3 border border-sand/60"
                  >
                    <label className="flex items-center gap-3 w-32 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={open}
                        onChange={() => toggleDay(loc.id, d.k)}
                        className="accent-navy h-4 w-4"
                        data-testid={`hours-toggle-${loc.slug}-${d.k}`}
                      />
                      <span className={`text-sm ${open ? "text-ink" : "text-ink-soft"}`}>{d.name}</span>
                    </label>
                    {open ? (
                      <div className="flex items-center gap-2 flex-1 text-sm">
                        <select
                          value={range[0]}
                          onChange={(e) => setHour(loc.id, d.k, 0, e.target.value)}
                          data-testid={`hours-start-${loc.slug}-${d.k}`}
                          className="bg-cream-soft border border-sand rounded-lg px-2 py-1.5 text-sm"
                        >
                          {Array.from({ length: 24 }).map((_, h) => (
                            <option key={h} value={h}>{HOUR_LABEL(h)}</option>
                          ))}
                        </select>
                        <span className="text-ink-soft text-xs">to</span>
                        <select
                          value={range[1]}
                          onChange={(e) => setHour(loc.id, d.k, 1, e.target.value)}
                          data-testid={`hours-end-${loc.slug}-${d.k}`}
                          className="bg-cream-soft border border-sand rounded-lg px-2 py-1.5 text-sm"
                        >
                          {Array.from({ length: 25 }).map((_, h) => (
                            <option key={h} value={h}>{HOUR_LABEL(h % 24)}</option>
                          ))}
                        </select>
                        <span className="text-xs text-ink-soft ml-2">
                          {range[1] - range[0]} hour{range[1] - range[0] === 1 ? "" : "s"}
                        </span>
                      </div>
                    ) : (
                      <div className="text-sm text-ink-soft italic">Closed</div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end mt-5">
              <button
                onClick={() => save(loc)}
                disabled={!dirty || savingId === loc.id}
                data-testid={`hours-save-${loc.slug}`}
                className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-6 py-2.5 text-sm font-medium hover:bg-navy-deep transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {savingId === loc.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {dirty ? "Save changes" : "Saved"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
